import { useEffect } from "react";
import { get } from "lodash";
import MuiCheckbox from "@mui/material/Checkbox";
import type { CheckboxProps as MuiCheckboxProps } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import type { FormControlLabelProps } from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import { FormControl } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useFormikContext } from "formik";
import type { ReactNode } from "react";

import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";

type FormValues = Record<string, unknown>;

export interface CheckboxProps extends Omit<MuiCheckboxProps, "error" | "readOnly"> {
  name: string;
  label?: ReactNode;
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  labelSx?: SxProps<Theme>;
  loading?: boolean;
  helperText?: ReactNode;
  error?: string;
  readonly?: boolean;
}

export function Checkbox({
  name,
  color,
  size,
  icon,
  label,
  labelPlacement = "end",
  labelSx,
  checkedIcon,
  checked,
  onChange,
  inputProps,
  indeterminate,
  loading,
  helperText,
  sx,
  error,
  readonly,
}: CheckboxProps) {
  const formik = useFormikContext<FormValues>();
  const formContext = useFormContext();

  useEffect(() => {
    formik.setFieldError(name, error);
  }, [error]);

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <FormControl error={Boolean(get(formik.errors, name))}>
        <FormControlLabel
          label={label}
          labelPlacement={labelPlacement}
          sx={labelSx}
          control={
            <MuiCheckbox
              name={name}
              color={color}
              size={size}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={(checked ?? get(formik.values, name, null)) as boolean}
              onChange={onChange ?? formik.handleChange}
              inputProps={inputProps}
              indeterminate={indeterminate}
              sx={sx}
              disabled={readonly}
            />
          }
        />
        {Boolean(get(formik.errors, name)) && (
          <FormHelperText>{get(formik.errors, name) as ReactNode}</FormHelperText>
        )}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </FormSkeleton>
  );
}
